import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { validatePermission } from 'actions';
import styled from 'styled-components';
import Card from 'v3/components/Card';
import Button from 'v3/components/Button';
import Fields from './Fields';
import History from './History';
import Summary, { StatusPill } from './Summary';
import { changeForm, fromResponse, initialForm, toPayload, validateForm } from './form';
import { controlTowerService, errorMessage } from './service';

const SAVED = 'Disponibilidade salva.';
const Body = styled.div`
  .tc-notice {
    margin: 0;
    padding: 12px 24px;
    font-size: 13px;
    border-bottom: 1px solid #e5eaee;
  }
  .tc-edit {
    padding: 20px 24px;
  }
  .tc-history {
    padding: 14px 24px 18px;
    border-top: 1px solid #e5eaee;
  }
  .tc-conflict {
    margin: 16px 24px;
    padding: 16px;
    background: #fff7df;
  }
  @media (max-width: 520px) {
    .tc-notice,
    .tc-edit,
    .tc-history {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
`;
export const CardTitle = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
    <h2 style={{ fontSize: 18, margin: 0 }}>Disponibilidade e Recusas</h2>
    {children}
  </div>
);

export function ControlTowerEditor({ type, id, canEdit, canRead = true, historySection }) {
  const [form, setForm] = useState(initialForm);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(null);
  const sequence = useRef(0);
  const lock = useRef(false);
  async function load() {
    const ticket = ++sequence.current;
    setLoading(true);
    setMessage('');
    try {
      const data = await controlTowerService.read(type, id);
      const next = fromResponse(data);
      if (ticket !== sequence.current) return;
      setSaved(data);
      setForm(next);
      setEditing(false);
      setConflict(null);
      setErrors({});
    } catch (error) {
      if (ticket === sequence.current) setMessage(errorMessage(error));
    } finally {
      if (ticket === sequence.current) setLoading(false);
    }
  }
  useEffect(() => {
    if (canRead && id) load();
    else setLoading(false);
    return () => {
      sequence.current += 1;
    };
  }, [type, id, canRead]);
  async function save() {
    if (lock.current || !canEdit || conflict || !saved) return;
    const validation = validateForm(form);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    const ticket = sequence.current;
    lock.current = true;
    setSaving(true);
    setMessage('');
    try {
      const data = await controlTowerService.save(type, id, toPayload(form, saved.version));
      const next = fromResponse(data);
      if (ticket !== sequence.current) return;
      setSaved(data);
      setForm(next);
      setEditing(false);
      setMessage(SAVED);
    } catch (error) {
      if (ticket !== sequence.current) return;
      setMessage(errorMessage(error));
      if (error?.response?.data?.field)
        setErrors({ [error.response.data.field]: errorMessage(error) });
      if (error?.response?.status === 409) setConflict({ loading: false, data: null });
    } finally {
      lock.current = false;
      if (ticket === sequence.current) setSaving(false);
    }
  }
  async function compare() {
    const ticket = sequence.current;
    setConflict({ loading: true, data: null });
    try {
      const data = await controlTowerService.read(type, id);
      fromResponse(data);
      if (ticket === sequence.current) setConflict({ data, loading: false });
    } catch (error) {
      if (ticket === sequence.current) {
        setMessage(errorMessage(error));
        setConflict({ loading: false, data: null });
      }
    }
  }
  return (
    <Card
      header={<CardTitle>{!loading && saved && <StatusPill data={saved} />}</CardTitle>}
      bodyCardPadding="0"
      data-testid="control-tower-card"
      HeaderRightComponent={
        !loading &&
        saved &&
        canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            {editing ? (
              <>
                <Button
                  variant="secondary"
                  disabled={saving}
                  onClick={() => {
                    setForm(fromResponse(saved));
                    setEditing(false);
                    setConflict(null);
                    setErrors({});
                    setMessage('');
                  }}
                >
                  Cancelar
                </Button>
                <Button variant="primary" loading={saving} disabled={!!conflict} onClick={save}>
                  Salvar
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(true);
                  setMessage('');
                }}
              >
                Editar
              </Button>
            )}
          </div>
        )
      }
    >
      <Body>
        {!canRead ? (
          <p className="tc-notice" role="status">
            Sem permissão para consultar a disponibilidade da placa.
          </p>
        ) : loading ? (
          <p className="tc-notice" role="status">
            Carregando disponibilidade...
          </p>
        ) : (
          <>
            {message && (
              <p className="tc-notice" role={message === SAVED ? 'status' : 'alert'}>
                {message}
              </p>
            )}
            {!saved ? (
              <div className="tc-edit">
                <Button variant="secondary" onClick={load}>
                  Tentar novamente
                </Button>
              </div>
            ) : (
              <>
                {saved.updateSource === 'SYSTEM' && (
                  <p className="tc-notice">Última atualização automática pelo sistema.</p>
                )}
                {conflict && (
                  <div role="alert" className="tc-conflict">
                    <p>
                      O cadastro foi alterado. Seu rascunho foi preservado. Consulte os dados atuais
                      antes de continuar.
                    </p>
                    <Button variant="secondary" loading={conflict.loading} onClick={compare}>
                      Consultar dados atuais
                    </Button>
                    {conflict.data && (
                      <>
                        <p>
                          Dados atuais salvos: <StatusPill data={conflict.data} />
                        </p>
                        <Summary form={fromResponse(conflict.data)} />
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setSaved(conflict.data);
                            setForm(fromResponse(conflict.data));
                            setConflict(null);
                            setEditing(false);
                            setErrors({});
                            setMessage('');
                          }}
                        >
                          Usar dados atuais e descartar rascunho
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {editing ? (
                  <div className="tc-edit">
                    <Fields
                      form={form}
                      change={(field, value) => {
                        setForm((old) => changeForm(old, field, value));
                        setErrors({});
                      }}
                      errors={errors}
                      disabled={saving}
                    />
                  </div>
                ) : (
                  <Summary form={form} />
                )}
                {historySection !== false && (
                  <div className="tc-history">
                    {historySection ?? <History key={`${type}:${id}`} type={type} id={id} />}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Body>
    </Card>
  );
}
export default function ControlTowerCard({ type, id, historySection }) {
  const company = useSelector((state) => state.currentCompany);
  const prefix = type === 'vehicle' ? 'VEICULOS' : 'IMPLEMENTO';
  return (
    <ControlTowerEditor
      key={`${type}:${id}:${JSON.stringify(company)}`}
      type={type}
      id={id}
      historySection={historySection}
      canRead={validatePermission(`VISUALIZAR_${prefix}`)}
      canEdit={validatePermission(type === 'vehicle' ? 'ALTERAR_VEICULOS' : 'EDITAR_IMPLEMENTO')}
    />
  );
}
